"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { AgentNetwork } from "@/components/marketing/landing-v2/AgentNetwork"
import { AutomationModules } from "@/components/marketing/landing-v2/AutomationModules"
import { ClosingBand } from "@/components/marketing/landing-v2/ClosingBand"
import { ContactPanel } from "@/components/marketing/landing-v2/ContactPanel"
import { DiscoveryPanel } from "@/components/marketing/landing-v2/DiscoveryPanel"
import { Landing2Footer } from "@/components/marketing/landing-v2/Landing2Footer"
import { Landing2Header } from "@/components/marketing/landing-v2/Landing2Header"
import { Landing2Hero } from "@/components/marketing/landing-v2/Landing2Hero"
import { PrinciplesSection } from "@/components/marketing/landing-v2/PrinciplesSection"
import { ProcessSection } from "@/components/marketing/landing-v2/ProcessSection"
import { QuestionPanel } from "@/components/marketing/landing-v2/QuestionPanel"
import { WorkflowSection } from "@/components/marketing/landing-v2/WorkflowSection"
import { LandingChatbotEmbed } from "@/components/marketing/LandingChatbotEmbed"
import { useLanding2Locale } from "@/hooks/useLanding2Locale"
import {
  buildQuestionFlow,
  type ContactMethodId,
  type Landing2Stage,
  type LandingCategoryId,
} from "@/lib/landing-v2/content"
import { getLanding2Copy } from "@/lib/landing-v2/copy"
import { getLandingSubmissionContext } from "@/lib/landing-client-context"

const THEME_STORAGE_KEY = "atirame-landing2-theme"

function themeRoot(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".landing2-theme")
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
}

export default function Landing2Page() {
  const { locale, setLocale } = useLanding2Locale()
  const copy = getLanding2Copy(locale)

  const [theme, setTheme] = useState<"dark" | "light">("dark")
  const [selected, setSelected] = useState<LandingCategoryId[]>([])
  const [categoryText, setCategoryText] = useState("")
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [freeTexts, setFreeTexts] = useState<Record<string, string>>({})
  const [skippedAhead, setSkippedAhead] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submittedPartial, setSubmittedPartial] = useState(false)

  // The bootstrap script in the route layout already applied the stored theme.
  useEffect(() => {
    setTheme(themeRoot()?.dataset.theme === "light" ? "light" : "dark")
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next = current === "dark" ? "light" : "dark"
      const root = themeRoot()
      if (root) root.dataset.theme = next
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next)
      } catch {
        // Private browsing modes can reject storage; the toggle still works for the session.
      }
      return next
    })
  }, [])

  const steps = useMemo(() => buildQuestionFlow(selected), [selected])
  const activeIndex = steps.length > 0 ? Math.min(questionIndex, steps.length - 1) : 0
  const activeStep = steps[activeIndex]
  const allAnswered = steps.length > 0 && steps.every((step) => Boolean(answers[step.key]))
  const primaryCategory: LandingCategoryId = selected[0] ?? "ecommerce"

  const stage: Landing2Stage = submitted
    ? "done"
    : skippedAhead || allAnswered
      ? "contact"
      : selected.length > 0
        ? "questions"
        : "categories"

  const toggleCategory = useCallback((categoryId: LandingCategoryId) => {
    setSelected((current) =>
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId],
    )
    setQuestionIndex(0)
  }, [])

  const answerCurrent = useCallback(
    (optionId: string) => {
      if (!activeStep) return
      setAnswers((current) => ({ ...current, [activeStep.key]: optionId }))
      // Answering again means the visitor came back from the skip-ahead shortcut.
      setSkippedAhead(false)
    },
    [activeStep],
  )

  const setCurrentFreeText = useCallback(
    (value: string) => {
      if (!activeStep) return
      setFreeTexts((current) => ({ ...current, [activeStep.key]: value }))
    },
    [activeStep],
  )

  const goNext = useCallback(() => {
    if (activeIndex < steps.length - 1) {
      setQuestionIndex(activeIndex + 1)
      return
    }
    scrollToSection("contact")
  }, [activeIndex, steps.length])

  const goBack = useCallback(() => {
    setQuestionIndex((current) => Math.max(0, current - 1))
  }, [])

  const skipToContact = useCallback(() => {
    setSkippedAhead(true)
    scrollToSection("contact")
  }, [])

  const submitContact = useCallback(
    async (method: ContactMethodId, value: string) => {
      const parts: string[] = []
      if (selected.includes("other") && categoryText.trim()) {
        parts.push(categoryText.trim())
      }
      for (const step of steps) {
        const note = freeTexts[step.key]?.trim()
        if (note) parts.push(`${copy.questionText[step.copyKey] ?? step.copyKey} ${note}`)
      }

      const isComplete = allAnswered
      const { sessionId, widgetPhone } = getLandingSubmissionContext()

      try {
        const response = await fetch("/api/landing/discovery", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contactMethod: method,
            contactValue: value,
            categories: selected.length > 0 ? selected : ["other"],
            answers,
            freeText: parts.length > 0 ? parts.join("\n") : undefined,
            locale,
            submissionType: isComplete ? "complete" : "partial",
            sessionId,
            widgetPhone,
          }),
        })
        if (!response.ok) return false
      } catch {
        return false
      }

      setSubmittedPartial(!isComplete)
      setSubmitted(true)
      return true
    },
    [allAnswered, answers, categoryText, copy.questionText, freeTexts, locale, selected, steps],
  )

  const goToDiscovery = useCallback(() => scrollToSection("discovery"), [])

  return (
    <div className={`l2-root${locale === "hy" ? " l2-locale-hy" : ""}`} lang={locale}>
      <LandingChatbotEmbed locale={locale} />
      <a className="l2-skip" href="#discovery">
        {copy.nav.cta}
      </a>

      <Landing2Header
        copy={copy}
        locale={locale}
        onLocaleChange={setLocale}
        theme={theme}
        onThemeToggle={toggleTheme}
        onCta={goToDiscovery}
      />

      <main>
        <Landing2Hero copy={copy} onCta={goToDiscovery} />

        <WorkflowSection copy={copy} categoryId={primaryCategory} />

        <AutomationModules copy={copy} />

        <DiscoveryPanel
          copy={copy}
          stage={stage}
          selected={selected}
          onToggle={toggleCategory}
          otherText={categoryText}
          onOtherTextChange={setCategoryText}
          onContinue={() => scrollToSection("questions")}
        />

        {steps.length > 0 && (
          <QuestionPanel
            copy={copy}
            stage={stage}
            steps={steps}
            index={activeIndex}
            answers={answers}
            freeText={activeStep ? (freeTexts[activeStep.key] ?? "") : ""}
            onAnswer={answerCurrent}
            onFreeTextChange={setCurrentFreeText}
            onBack={goBack}
            onNext={goNext}
            onEscape={skipToContact}
          />
        )}

        <ContactPanel
          copy={copy}
          locale={locale}
          stage={stage}
          categoryId={primaryCategory}
          submitted={submitted}
          partial={submittedPartial}
          onSubmit={submitContact}
          onReset={() => setSubmitted(false)}
        />

        <AgentNetwork copy={copy} />

        <PrinciplesSection copy={copy} />

        <ProcessSection copy={copy} />

        <ClosingBand copy={copy} onCta={goToDiscovery} />
      </main>

      <Landing2Footer copy={copy} locale={locale} onLocaleChange={setLocale} />
    </div>
  )
}
